import * as React from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

import { Button } from "./button";
import { Dialog, DialogContent, DialogTitle } from "./dialog";

/**
 * ImageLightbox (TES-2214) — full-screen prev/next viewer that pairs with
 * `ImageGalleryGrid`/`GalleryImageTile`. REINE UI-HUELLE: der Aufrufer liefert
 * je Bild einen fertig gerenderten Slot (z. B. eine auth-gefetchte
 * Image-Komponente oder ein einfaches `<img>`) sowie fertige i18n-Strings fuer
 * Titel/Labels. URL-Aufloesung, Auth und Copy bleiben in der App (kein
 * zweiter Bildpfad, keine CheckMaty-Semantik im Package).
 *
 * Nur das jeweils aktive `images[currentIndex]`-Element wird tatsaechlich in
 * den Baum eingehaengt — die uebrigen Slots sind React-Elemente (billig),
 * keine gemounteten DOM-Knoten.
 */
export interface ImageLightboxProps {
  /** Ein fertig gerenderter Slot pro Bild (Reihenfolge = Navigationsreihenfolge). */
  images: React.ReactNode[];
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Accessible Dialog-Titel (sr-only) — fertiger i18n-String. */
  title: string;
  /** aria-label des Schliessen-Buttons — fertiger i18n-String. */
  closeLabel?: string;
  /** aria-label des "voriges Bild"-Buttons — fertiger i18n-String. */
  previousLabel?: string;
  /** aria-label des "naechstes Bild"-Buttons — fertiger i18n-String. */
  nextLabel?: string;
  /** aria-label je Indikator-Dot, z. B. `(index) => \`Bild ${index + 1}\`` . */
  indicatorLabel?: (index: number) => string;
}

export function ImageLightbox({
  images,
  initialIndex = 0,
  open,
  onOpenChange,
  title,
  closeLabel,
  previousLabel,
  nextLabel,
  indicatorLabel,
}: ImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);

  // Snap back to initialIndex whenever the lightbox (re-)opens.
  React.useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  if (!images.length) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* TES-1735 (N11)-Konvention: Radix DialogContent braucht einen DialogTitle als
          accessible Name (sr-only, visuell traegt das Bild selbst); aria-describedby
          explizit undefined, da es keine Beschreibung gibt (unterdrueckt die Radix-Warnung). */}
      <DialogContent className="max-w-4xl p-0 bg-black/95 border-none" aria-describedby={undefined}>
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="relative flex items-center justify-center min-h-[60vh]">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 text-white hover:bg-white/20 z-10"
            onClick={() => onOpenChange(false)}
            aria-label={closeLabel}
          >
            <X className="h-5 w-5" />
          </Button>

          {images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 text-white hover:bg-white/20"
                onClick={goToPrevious}
                aria-label={previousLabel}
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 text-white hover:bg-white/20"
                onClick={goToNext}
                aria-label={nextLabel}
              >
                <ChevronRight className="h-8 w-8" />
              </Button>
            </>
          )}

          {images[currentIndex]}

          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  aria-label={indicatorLabel?.(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentIndex ? "bg-white" : "bg-white/40"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
